<template>
  <WdsModal
    :show="show"
    :title="$t('Approval request')"
    md
    @close="show = false"
    @cancel="show = false"
  >
    <WdsStack v-if="loading" justify-center>
      <BaseLoadingNotice />
    </WdsStack>
    <div v-else-if="approvable !== null">
      <div class="tw-mb-2">
        <ApprovalRequestState
          v-if="!!approvalApprovable?.approval"
          :approval-states="states"
          :workflow-details="approvalApprovable?.approval"
          :number-of-steps="numberOfSteps"
          :current-step="currentStep"
        />
      </div>
      <WdsAlert
        :title="$t('Your approval is required for the following reason(s):')"
        class="tw-mb-2"
      >
        <template #description>
          <ul>
            <li v-for="reason of triggerReasons" :key="reason">{{ reason }}</li>
          </ul>
        </template>
      </WdsAlert>
      <WdsDivider class="tw-my-6" align-left>
        <WdsText large>{{ $t('Details') }}</WdsText>
      </WdsDivider>
      <div class="tw-bg-true-gray-50 tw-p-3 tw-rounded tw-my-3">
        <component :is="approvableType" :approvable="approvable" />
      </div>
    </div>
    <div v-else>
      {{ $t('Failed to load approvable') }}
    </div>
    <WdsDivider class="tw-my-6" align-left>
      <WdsText large>{{ $t('Review') }}</WdsText>
    </WdsDivider>
    <WdsRadioGroup name="multiple">
      <WdsRadio
        v-model:checked="status"
        data-test="approve-radio"
        :value="ApprovalApprovableState.Approved"
        class="radio-top-adjust"
        :disabled="submitting"
      >
        <WdsText>
          {{ $t('Approve') }}
          <template #subText>
            {{
              $t('Select this option to move the request to the next step in the approval process.')
            }}
          </template>
        </WdsText>
      </WdsRadio>
      <WdsRadio
        v-model:checked="status"
        data-test="deny-radio"
        :value="ApprovalApprovableState.Rejected"
        class="radio-top-adjust"
        :disabled="submitting"
      >
        <WdsText>
          {{ $t('Deny') }}
          <template #subText>
            {{
              $t(
                'Select this option to stop the process and cancel the request. You need to specify the reason in the comment field below.',
              )
            }}
          </template>
        </WdsText>
      </WdsRadio>
    </WdsRadioGroup>
    <div class="tw-my-5">
      <WdsInputArea
        v-model="reason"
        :disabled="submitting"
        :placeholder="$t('Add a comment or a reason for your review decision...')"
        :error="feedbackHintText"
      >
        <template #label>
          <WdsStack gap-md>
            {{ $t('Comment') }}
            <WdsTag v-if="status === ApprovalApprovableState.Rejected" sm>
              {{ $t('Required') }}
            </WdsTag>
          </WdsStack>
        </template>
      </WdsInputArea>
    </div>
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton @click="show = false">{{ $t('Cancel') }}</WdsButton>
        <div class="wds-footer-action-buttons">
          <WdsStack gap-sm align-center>
            <WdsSpinner v-if="submitting" indeterminate />
            <WdsButton
              data-test="submit-feedback-button"
              primary
              :disabled="submitDisabled"
              @click="submit"
            >
              {{ $t('Confirm') }}
            </WdsButton>
          </WdsStack>
        </div>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsButton,
  WdsDivider,
  WdsInputArea,
  WdsModal,
  WdsRadio,
  WdsRadioGroup,
  WdsSpinner,
  WdsStack,
  WdsTag,
  WdsText,
} from '@worksome/wds-components'
import { computed, ComputedRef, ref, Ref, watch, WritableComputedRef } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { useVModel } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import {
  ApprovalStateFragment,
  HireApprovableFragment,
  WorkflowDetailsFragment,
} from '@/components/Workflows/Approvals/graphql/fragments'
import { FragmentType, graphql, useFragment } from '@/gql'
import HireApprovalRequestDisplay from '@/components/Workflows/Approvals/Triggers/Hire/ApprovalRequestDisplay.vue'
import ApprovalRequestState from '@/components/Workflows/Approvals/Request/ApprovalRequestState.vue'
import {
  ApprovalApprovableState,
  ApprovalTrigger,
  MarketCode,
  WorkflowVariableOperator,
} from '@/gql/graphql'
import ClassificationApprovalRequestDisplay from '@/components/Workflows/Approvals/Triggers/Classification/ApprovalRequestDisplay.vue'
import { NetworkError } from '@/utils/notification'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import { determinationResultToText } from '@/components/Workflows/Approvals/utilities/classificationResultToText'

const props = defineProps<{
  approvalApprovableId: string
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [state: boolean]
  'refresh-approvables': []
}>()

const reason = ref('')
const status = ref()
const show: Ref<boolean> | WritableComputedRef<boolean> = useVModel(props, 'show', emit)

// Any time we hide the modal we reset the reason value otherwise feedback can be show across different approval request
watch(show, (value) => {
  if (!value) {
    reason.value = ''
  }
})

const attemptedToSubmit = ref(false)
const feedbackHintText = computed(() => {
  if (attemptedToSubmit.value) {
    return $t('Feedback is required to reject the request')
  }
  return undefined // Removes the error props fully
})

const { result, loading } = useQuery(
  graphql(/* GraphQL */
  `
    query Approval($approvable: ID!) {
      approvalApprovable(id: $approvable) {
        id
        approvalRule {
          id
          rules {
            id
            variable {
              id
              title
              ... on WorkflowVariableOptionType {
                options {
                  label
                  value
                }
              }
            }
            value
            operator
          }
          approvers {
            id
          }
        }
        approval {
          ...WorkflowDetailsFragment
        }
        approvable {
          ... on Hire {
            ...HireApprovableFragment
          }
        }
        approvalStates {
          id
          state
          createdAt
          ...ApprovalStateFragment
        }
      }
    }
  `),
  () => ({
    approvable: props.approvalApprovableId,
  }),
  {
    clientId: 'publicApiCustomFields',
    enabled: show,
  },
)

const { mutate, loading: submitting } = useMutation(
  graphql(/* GraphQL */ `
    mutation ActionApproval($input: ActionApprovalApprovableInput!) {
      actionApprovalApprovable(input: $input) {
        id
      }
    }
  `),
  {
    clientId: 'publicApiCustomFields',
    refetchQueries: [
      'Approvables',
      'approvalApprovables',
      'TableReviewedApprovals',
      'approvalStates',
    ],
  },
)

const submitDisabled = computed(() => {
  const hasStatus = !status.value
  const needsFeedback = status.value === ApprovalApprovableState.Rejected && reason.value === ''

  return hasStatus || submitting.value || needsFeedback
})

const submit = async () => {
  try {
    if (status.value === ApprovalApprovableState.Rejected && reason.value === '') {
      attemptedToSubmit.value = true
      return
    }

    await mutate({
      input: {
        id: props.approvalApprovableId,
        reason: reason.value,
        status: status.value,
      },
    })
    show.value = false
    emit('refresh-approvables')
  } catch (ex: any) {
    new NetworkError({ ex }).dispatch()
  }
}

const approvalApprovable = computed(() => result.value?.approvalApprovable)

const workflowDetails = computed(() =>
  useFragment(WorkflowDetailsFragment, approvalApprovable.value?.approval),
)

const approvable: ComputedRef<FragmentType<typeof HireApprovableFragment> | null> = computed(
  () => approvalApprovable.value?.approvable ?? null,
)

const market: ComputedRef<MarketCode | undefined> = computed(
  () => useFragment(HireApprovableFragment, approvable.value)?.job.market,
)

// This type adds the id to our typing in the template
type StateType = FragmentType<typeof ApprovalStateFragment> & { id: string }
const states: ComputedRef<StateType[]> = computed(
  () => approvalApprovable.value?.approvalStates ?? [],
)

const currentState = computed(() =>
  states.value.length > 0
    ? useFragment(ApprovalStateFragment, states.value[states.value.length - 1])
    : null,
)

const numberOfSteps: ComputedRef<number> = computed(
  () => approvalApprovable.value?.approvalRule?.approvers.length ?? 0,
)

const currentStep: ComputedRef<number> = computed(
  () =>
    (approvalApprovable.value?.approvalRule?.approvers.findIndex(
      ({ id }) => id === currentState.value?.approver.id,
    ) ?? -1) + 1,
)

const triggerReasonGroup: ComputedRef<string[]> = computed(() =>
  $t('You are in “:groupname” approval group', {
    groupname: currentState.value?.approver.userGroup.name,
  }),
)

const triggerReasons: ComputedRef<string[]> = computed(
  () =>
    approvalApprovable.value?.approvalRule.rules
      ?.map<string>((rule) => {
        const { title, id, __typename } = rule.variable
        const { operator, value } = rule ?? {}

        // If we are an option type, lets find the options label to display as it makes much more sense to the
        // user when reading the reasons
        let label =
          __typename === 'WorkflowVariableOptionType'
            ? rule.variable.options.find((opt) => opt.value === value)?.label ?? value
            : value

        // Covers the edge case of it being the classification result system variable here
        if (id === 'classification-result') {
          label = determinationResultToText(value)
        }

        // Default case is here
        let ops: string = ''
        switch (operator) {
          case WorkflowVariableOperator.Equals:
            ops = $t('equals')
            break
          case WorkflowVariableOperator.NotEquals:
            ops = $t('does not equals')
            break
          case WorkflowVariableOperator.Greater:
            ops = $t('is more than')
            break
          case WorkflowVariableOperator.GreaterOrEquals:
            ops = $t('is more than or equals')
            break
          case WorkflowVariableOperator.Less:
            ops = $t('is less than')
            break
          case WorkflowVariableOperator.LessOrEquals:
            ops = $t('is less than or equals')
            break
          case WorkflowVariableOperator.Contains:
            ops = $t('contains')
            break
          case WorkflowVariableOperator.DoesNotContain:
            ops = $t('does not contain')
            break
          case WorkflowVariableOperator.Changed:
            ops = $t('has changed')
            break
        }

        return $t(':fieldTitle :operator :value', {
          fieldTitle: title,
          operator: ops,
          value: label,
        })
      })
      .concat(triggerReasonGroup.value) ?? triggerReasonGroup.value,
)

// Map the approvable data to its relevant component.
const approvableType = computed(() => {
  switch (workflowDetails.value?.trigger) {
    case ApprovalTrigger.HireCreated:
    case ApprovalTrigger.HireChanged:
      return HireApprovalRequestDisplay
    case ApprovalTrigger.ClassificationCreated:
      return ClassificationApprovalRequestDisplay
    default:
      return 'div'
  }
})
</script>

<style lang="scss" scoped>
.radio-top-adjust {
  &:deep(.wds-radio-input) {
    &:first-child {
      margin-top: 3px; // Pushes the radio button just a smidgen to get into position
    }
  }
}
</style>
